import { useEffect, useState } from 'react';
import { supabase } from '../data/supabaseClient';
import { useChat } from './ChatContext';
import { useTranslation } from 'react-i18next';
import { useToast } from '../hooks/ToastContext';

export const NotificationType = {
  NEW_MESSAGE: 'new_message',
  RIDE_REQUEST: 'ride_request',
  REQUEST_ACCEPTED: 'request_accepted',
  REQUEST_DECLINED: 'request_declined',
  RIDE_COMPLETED: 'ride_completed',
  RIDE_CANCELLED: 'ride_cancelled',
  NEW_REVIEW: 'new_review'
};

const useRideNotifications = (userId) => {
  const { t } = useTranslation('common');
  const { addNotification } = useChat();
  const { addToast } = useToast();
  const [error, setError] = useState(null);
  const [channels, setChannels] = useState([]);

  useEffect(() => {
    if (!userId) return;

    let messageChannel = null;
    let notificationsChannel = null;

    const setupSubscriptions = async () => {
      try {
        // Subscribe to new messages
        messageChannel = supabase
          .channel('new-messages')
          .on('postgres_changes', 
            {
              event: 'INSERT',
              schema: 'public',
              table: 'messages'
            },
            async (payload) => {
              // Only proceed if message is not from current user
              if (payload.new.user_id === userId) return;

              try {
                // Get conversation details
                const { data: conversation, error: convoError } = await supabase
                  .from('conversations')
                  .select(`
                    *,
                    profiles!conversations_participant_ids_fkey(
                      username,
                      avatar_url
                    )
                  `)
                  .eq('id', payload.new.conversation_id)
                  .single();

                if (convoError && convoError.code !== 'PGRST116') {
                  console.error('Error fetching conversation:', convoError);
                  return;
                }

                // Get sender's profile
                const { data: senderProfile, error: profileError } = await supabase
                  .from('profiles')
                  .select('username, avatar_url')
                  .eq('id', payload.new.user_id)
                  .single();

                if (profileError && profileError.code !== 'PGRST116') {
                  console.error('Error fetching sender profile:', profileError);
                  return;
                }

                if (!senderProfile) {
                  console.warn('Sender profile not found');
                  return;
                }

                // Add notification
                addNotification({
                  type: NotificationType.NEW_MESSAGE,
                  conversationId: payload.new.conversation_id,
                  sender: senderProfile.username || t('notifications.unknown_user'),
                  message: payload.new.text,
                  timestamp: new Date().toISOString(),
                  avatarUrl: senderProfile.avatar_url
                });

              } catch (error) {
                console.error('Error processing message notification:', error);
                setError(error.message);
              }
            }
          );

        // Subscribe to notifications table
        notificationsChannel = supabase
          .channel('user-notifications')
          .on('postgres_changes', 
            {
              event: 'INSERT',
              schema: 'public',
              table: 'notifications',
              filter: `user_id=eq.${userId}`
            },
            async (payload) => {
              try {
                // Validate notification data
                if (!payload.new || !payload.new.type) {
                  console.warn('Invalid notification data received:', payload);
                  return;
                }

                // Get sender's profile if it exists
                let senderName = t('notifications.system');
                let avatarUrl = null;

                if (payload.new.sender_id) {
                  const { data: senderProfile, error: profileError } = await supabase
                    .from('profiles')
                    .select('username, avatar_url')
                    .eq('id', payload.new.sender_id)
                    .single();

                  if (profileError && profileError.code !== 'PGRST116') {
                    console.error('Error fetching sender profile:', profileError);
                  }

                  if (senderProfile) {
                    senderName = senderProfile.username;
                    avatarUrl = senderProfile.avatar_url;
                  }
                }

                // Toast creation removed - NotificationContext.js handles all toasts to prevent duplicates
                // Only keep the chat notification logic below

                // Add notification to chat notifications
                addNotification({
                  type: payload.new.type,
                  id: payload.new.id,
                  message: payload.new.message || getDefaultMessage(payload.new.type, senderName, t),
                  sender: senderName,
                  timestamp: payload.new.created_at,
                  avatarUrl: avatarUrl,
                  relatedId: payload.new.related_id
                });

              } catch (error) {
                console.error('Error processing notification:', error);
                setError(error.message);
              }
            }
          );

        // Subscribe to channels
        const subscribeToChannel = async (channel) => {
          try {
            await channel.subscribe();
            return channel;
          } catch (error) {
            console.error(`Error subscribing to channel:`, error);
            setError(t('notifications.error_subscription'));
            return null;
          }
        };

        const msgChannel = await subscribeToChannel(messageChannel);
        const notifChannel = await subscribeToChannel(notificationsChannel);
        
        if (msgChannel && notifChannel) {
          setChannels([msgChannel, notifChannel]);
        } else {
          // If subscription failed, attempt to remove any channels that did subscribe
          if (msgChannel) supabase.removeChannel(msgChannel);
          if (notifChannel) supabase.removeChannel(notifChannel);
          
          setError(t('notifications.error_subscription'));
          console.error('Failed to subscribe to notification channels');
        }
      } catch (error) {
        console.error('Error setting up notification channels:', error);
        setError(error.message);
      }
    };

    setupSubscriptions();

    // Cleanup subscriptions
    return () => {
      // Clean up any open channels
      channels.forEach(channel => {
        try {
          supabase.removeChannel(channel);
        } catch (error) {
          console.error('Error removing channel:', error);
        }
      });
    };
  }, [userId, addNotification, addToast, t]);

  // Helper function to get appropriate toast type for a notification type
  const getToastTypeForNotification = (notificationType) => {
    switch (notificationType) {
      case NotificationType.RIDE_REQUEST:
        return 'ride_request';
      case NotificationType.REQUEST_ACCEPTED:
        return 'success';
      case NotificationType.REQUEST_DECLINED:
        return 'error';
      case NotificationType.RIDE_CANCELLED:
        return 'warning';
      case NotificationType.RIDE_COMPLETED:
        return 'success';
      case NotificationType.NEW_REVIEW:
        return 'info';
      case NotificationType.NEW_MESSAGE:
        return 'new_conversation';
      default:
        return 'info';
    }
  };

  // If there was an error setting up notifications, log it but don't prevent the component from mounting
  if (error) {
    console.error('RideNotifications setup error:', error);
    // We can add an error state here if needed, but for now we'll just log it
  }

  return null; // This hook doesn't render anything
};

// Helper functions for notification text

// Get notification title based on type
const getNotificationTitle = (type, t) => {
  const titles = {
    'ride_request': t('notifications.ride_request'),
    'request_accepted': t('notifications.ride_accepted'),
    'request_declined': t('notifications.ride_declined'),
    'ride_cancelled': t('notifications.ride_cancelled'),
    'ride_completed': t('notifications.ride_completed'),
    'new_review': t('notifications.new_review'),
    'new_message': t('notifications.new_message'),
    'new_conversation': t('notifications.new_conversation')
  };
  return titles[type] || t('notifications.notification');
};

// Get default message for notification types
const getDefaultMessage = (type, senderName, t) => {
  switch (type) {
    case 'ride_request':
      return t('notifications.ride_request_message', { sender: senderName });
    case 'request_accepted':
      return t('notifications.ride_accepted_message', { sender: senderName });
    case 'request_declined':
      return t('notifications.ride_declined_message', { sender: senderName });
    case 'ride_cancelled':
      return t('notifications.ride_cancelled_message', { sender: senderName });
    case 'ride_completed':
      return t('notifications.ride_completed_message');
    case 'new_review':
      return t('notifications.new_review_message', { sender: senderName });
    case 'new_message':
    case 'new_conversation':
      return t('notifications.new_message_message', { sender: senderName });
    default:
      return t('notifications.default_message');
  }
};

// Helper function for creating notifications
export const createNotification = async (userId, type, message, relatedId = null, senderId = null) => {
  try {
    const { error } = await supabase
      .from('notifications')
      .insert([{
        user_id: userId,
        type,
        message,
        related_id: relatedId,
        sender_id: senderId,
        created_at: new Date().toISOString(),
        is_read: false
      }]);

    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error creating notification:', error);
    return false;
  }
};

export default useRideNotifications;